import React, { useState, useEffect } from 'react';

const PrayerRequestTracker = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({ total: 0, new: 0, praying: 0, answered: 0 });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const crmAuth = localStorage.getItem('crmAuth') || btoa('admin:kioo2025!');

  const [newRequest, setNewRequest] = useState({
    requester_name: '',
    requester_phone: '',
    requester_email: '',
    requester_country: '',
    request: '',
    category: 'general',
    is_urgent: false,
    is_confidential: false,
    source: 'phone_call'
  });

  const categories = [
    { value: 'general', label: '🙏 General Prayer', color: 'gray' },
    { value: 'healing', label: '💊 Healing & Health', color: 'red' },
    { value: 'family', label: '👨‍👩‍👧‍👦 Family', color: 'blue' },
    { value: 'financial', label: '💰 Financial', color: 'green' },
    { value: 'spiritual', label: '✝️ Spiritual Growth', color: 'purple' },
    { value: 'relationships', label: '❤️ Relationships', color: 'pink' },
    { value: 'work', label: '💼 Work & Career', color: 'orange' },
    { value: 'salvation', label: '🕊️ Salvation', color: 'indigo' },
    { value: 'thanksgiving', label: '🙌 Thanksgiving', color: 'yellow' }
  ];

  const sources = [
    { value: 'phone_call', label: '📞 Phone Call' },
    { value: 'whatsapp', label: '💬 WhatsApp' },
    { value: 'website', label: '🌐 Website' },
    { value: 'in_person', label: '🏠 In Person' },
    { value: 'letter', label: '✉️ Letter' },
    { value: 'social_media', label: '📱 Social Media' }
  ];

  useEffect(() => {
    fetchRequests();
  }, [filterStatus]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const url = filterStatus === 'all' 
        ? `${BACKEND_URL}/api/crm/prayer-requests`
        : `${BACKEND_URL}/api/crm/prayer-requests?status=${filterStatus}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
        calculateStats(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching prayer requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (requestList) => {
    setStats({
      total: requestList.length,
      new: requestList.filter(r => r.status === 'new').length,
      praying: requestList.filter(r => r.status === 'praying').length,
      answered: requestList.filter(r => r.status === 'answered').length
    });
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/prayer-requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newRequest,
          status: 'new'
        })
      });

      if (response.ok) {
        await fetchRequests();
        setShowAddModal(false);
        resetForm();
        alert('✅ Prayer request recorded!');
      } else {
        const error = await response.json();
        alert('Failed to save: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/prayer-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchRequests();
        if (newStatus === 'answered') {
          alert('🙌 Praise God! Prayer marked as answered!');
        }
      }
    } catch (error) {
      alert('Error updating status: ' + error.message);
    }
  };

  const handleAddFollowUp = async (requestId, note) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/prayer-requests/${requestId}/follow-up`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note, date: new Date().toISOString() })
      });

      if (response.ok) {
        fetchRequests();
        alert('✅ Follow-up added!');
      }
    } catch (error) {
      alert('Error adding follow-up: ' + error.message);
    }
  };

  const resetForm = () => {
    setNewRequest({
      requester_name: '',
      requester_phone: '',
      requester_email: '',
      requester_country: '',
      request: '',
      category: 'general',
      is_urgent: false,
      is_confidential: false,
      source: 'phone_call'
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      praying: 'bg-blue-100 text-blue-800 border-blue-300',
      answered: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    const labels = {
      new: '🆕 New',
      praying: '🙏 Praying',
      answered: '✅ Answered',
      closed: '📁 Closed'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.new}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getCategoryInfo = (categoryValue) => {
    return categories.find(c => c.value === categoryValue) || categories[0];
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🙏 Prayer Request Tracker</h2>
          <p className="text-gray-600 mt-1">Track and follow up on prayer requests from listeners</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          ➕ New Prayer Request
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div 
          className={`rounded-lg p-4 cursor-pointer transition-all ${filterStatus === 'all' ? 'bg-purple-100 ring-2 ring-purple-500' : 'bg-purple-50 hover:bg-purple-100'}`}
          onClick={() => setFilterStatus('all')}
        >
          <p className="text-sm text-purple-600">Total Requests</p>
          <p className="text-2xl font-bold text-purple-900">{stats.total}</p>
        </div>
        <div 
          className={`rounded-lg p-4 cursor-pointer transition-all ${filterStatus === 'new' ? 'bg-yellow-100 ring-2 ring-yellow-500' : 'bg-yellow-50 hover:bg-yellow-100'}`}
          onClick={() => setFilterStatus('new')}
        >
          <p className="text-sm text-yellow-600">🆕 New / Pending</p>
          <p className="text-2xl font-bold text-yellow-900">{stats.new}</p>
        </div>
        <div 
          className={`rounded-lg p-4 cursor-pointer transition-all ${filterStatus === 'praying' ? 'bg-blue-100 ring-2 ring-blue-500' : 'bg-blue-50 hover:bg-blue-100'}`}
          onClick={() => setFilterStatus('praying')}
        >
          <p className="text-sm text-blue-600">🙏 Being Prayed For</p>
          <p className="text-2xl font-bold text-blue-900">{stats.praying}</p>
        </div>
        <div 
          className={`rounded-lg p-4 cursor-pointer transition-all ${filterStatus === 'answered' ? 'bg-green-100 ring-2 ring-green-500' : 'bg-green-50 hover:bg-green-100'}`}
          onClick={() => setFilterStatus('answered')}
        >
          <p className="text-sm text-green-600">✅ Answered</p>
          <p className="text-2xl font-bold text-green-900">{stats.answered}</p>
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin text-4xl">⏳</div>
          <p className="mt-2 text-gray-500">Loading prayer requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-12 text-center">
          <span className="text-6xl">🙏</span>
          <p className="mt-4 text-gray-600">No prayer requests found</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Record First Prayer Request
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const category = getCategoryInfo(request.category);
            return (
              <div 
                key={request.id} 
                className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${request.is_urgent ? 'border-red-500' : 'border-purple-300'}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-gray-900">
                        {request.is_confidential ? '🔒 Confidential' : request.requester_name || 'Anonymous'}
                      </span>
                      {getStatusBadge(request.status)}
                      <span className={`px-2 py-1 text-xs bg-${category.color}-100 text-${category.color}-800 rounded-full`}>
                        {category.label}
                      </span>
                      {request.is_urgent && <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">🚨 Urgent</span>}
                    </div>
                    
                    <p className="text-gray-700 mb-3">{request.request}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      {request.requester_country && <span>📍 {request.requester_country}</span>}
                      <span>📅 {formatDate(request.created_at)}</span>
                      <span>{sources.find(s => s.value === request.source)?.label || request.source}</span>
                    </div>

                    {/* Follow-ups */}
                    {request.follow_ups && request.follow_ups.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm font-medium text-gray-600 mb-2">Follow-ups:</p>
                        {request.follow_ups.map((fu, idx) => (
                          <div key={idx} className="text-sm text-gray-600 bg-gray-50 p-2 rounded mb-1">
                            <span className="text-gray-400">{formatDate(fu.date)}:</span> {fu.note}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    {request.status === 'new' && (
                      <button
                        onClick={() => handleUpdateStatus(request.id, 'praying')}
                        className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                      >
                        🙏 Start Praying
                      </button>
                    )}
                    {request.status === 'praying' && (
                      <button
                        onClick={() => handleUpdateStatus(request.id, 'answered')}
                        className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                      >
                        ✅ Mark Answered
                      </button>
                    )}
                    <button
                      onClick={() => {
                        const note = prompt('Enter follow-up note:');
                        if (note) handleAddFollowUp(request.id, note);
                      }}
                      className="px-3 py-1 text-sm bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100"
                    >
                      📝 Add Note
                    </button>
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="px-3 py-1 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100"
                    >
                      👁️ View
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Request Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">🙏 New Prayer Request</h2>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
              </div>

              <form onSubmit={handleCreateRequest} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Requester Name</label>
                    <input
                      type="text"
                      value={newRequest.requester_name}
                      onChange={(e) => setNewRequest({ ...newRequest, requester_name: e.target.value })}
                      placeholder="Leave blank for anonymous"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone / WhatsApp</label>
                    <input
                      type="tel"
                      value={newRequest.requester_phone}
                      onChange={(e) => setNewRequest({ ...newRequest, requester_phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={newRequest.requester_email}
                      onChange={(e) => setNewRequest({ ...newRequest, requester_email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <select
                      value={newRequest.requester_country}
                      onChange={(e) => setNewRequest({ ...newRequest, requester_country: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select Country</option>
                      <option value="Liberia">🇱🇷 Liberia</option>
                      <option value="Sierra Leone">🇸🇱 Sierra Leone</option>
                      <option value="Guinea">🇬🇳 Guinea</option>
                      <option value="Other">🌍 Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prayer Request *</label>
                  <textarea
                    required
                    value={newRequest.request}
                    onChange={(e) => setNewRequest({ ...newRequest, request: e.target.value })}
                    rows={4}
                    placeholder="Describe the prayer request..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={newRequest.category}
                      onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                    <select
                      value={newRequest.source}
                      onChange={(e) => setNewRequest({ ...newRequest, source: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      {sources.map(src => (
                        <option key={src.value} value={src.value}>{src.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newRequest.is_urgent}
                      onChange={(e) => setNewRequest({ ...newRequest, is_urgent: e.target.checked })}
                      className="mr-2"
                    />
                    🚨 Urgent Request
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newRequest.is_confidential}
                      onChange={(e) => setNewRequest({ ...newRequest, is_confidential: e.target.checked })}
                      className="mr-2"
                    />
                    🔒 Keep Confidential
                  </label>
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {loading ? '⏳ Saving...' : '🙏 Save Prayer Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrayerRequestTracker;
